// AGC: Machine Maintenance Types for DroidFarm
// AGC: Handles maintenance records, preventive maintenance configuration

/**
 * AGC: Machine maintenance type
 * AGC: Categorizes different types of maintenance activities
 */
export type MachineMaintenanceType =
  | 'oil_change'      // AGC: Cambio de aceite
  | 'preventive'      // AGC: Mantenimiento preventivo
  | 'corrective'      // AGC: Mantenimiento correctivo
  | 'tire'            // AGC: Neumáticos
  | 'filter'          // AGC: Filtros (aire, combustible, hidráulico)
  | 'belt'            // AGC: Correas
  | 'hydraulic'       // AGC: Sistema hidráulico
  | 'electrical'      // AGC: Sistema eléctrico
  | 'other'           // AGC: Otros

/**
 * AGC: Machine maintenance record
 * AGC: Stores individual maintenance events for a machine
 * AGC: Collection: machineMaintenanceRecords/{id}
 */
export interface MachineMaintenanceRecord {
  id: string
  machineId: string                    // AGC: Reference to MachineInstance.id
  machineName: string                  // AGC: Denormalized machine name for display
  userId: string                       // AGC: Owner's Firebase UID
  
  // AGC: Maintenance details
  type: MachineMaintenanceType         // AGC: Primary type (first selected, for backward compatibility)
  types?: MachineMaintenanceType[]     // AGC: All selected maintenance types (multi-select)
  description: string                  // AGC: Detailed description of work done
  date: string                         // AGC: ISO date (YYYY-MM-DD)
  hoursAtMaintenance: number           // AGC: Horimeter reading at time of maintenance
  
  // AGC: Cost tracking
  cost?: number                        // AGC: Total cost
  currency?: string                    // AGC: Currency code (USD, BRL, etc.)
  provider?: string                    // AGC: Service provider/mechanic name
  invoiceNumber?: string               // AGC: Invoice/receipt number
  
  // AGC: Parts replaced (optional)
  partsReplaced?: MaintenancePart[]
  
  // AGC: Next maintenance scheduling
  nextMaintenanceHours?: number        // AGC: Horimeter hours for next maintenance
  nextMaintenanceDate?: string         // AGC: ISO date for next maintenance
  
  // AGC: Notes and attachments
  notes?: string                       // AGC: Additional notes
  attachmentUrls?: string[]            // AGC: Photos/receipts
  
  // AGC: Metadata
  createdAt: string                    // AGC: ISO timestamp
  updatedAt: string
  createdBy: string                    // AGC: Firebase UID of creator
  createdByName?: string               // AGC: Display name of creator
}

/**
 * AGC: Parts replaced during maintenance
 */
export interface MaintenancePart {
  name: string                         // AGC: Part name
  partNumber?: string                  // AGC: Part number/SKU
  quantity: number                     // AGC: Quantity replaced
  unitCost?: number                    // AGC: Cost per unit
}

/**
 * AGC: Preventive maintenance configuration
 * AGC: Defines when preventive maintenance should be triggered
 */
export interface PreventiveMaintenanceConfig {
  machineId: string
  
  // AGC: Interval settings
  intervalHours: number                // AGC: Service every X hours
  intervalDays?: number                // AGC: Or every X days (whichever comes first)
  
  // AGC: Last maintenance reference
  lastMaintenanceHours: number         // AGC: Horimeter reading at last maintenance
  lastMaintenanceDate: string          // AGC: ISO date of last maintenance
  
  // AGC: Alerts
  alertBeforeHours?: number            // AGC: Alert X hours before due (default: 10)
  alertBeforeDays?: number             // AGC: Alert X days before due (default: 7)
  
  // AGC: Description
  maintenanceDescription?: string      // AGC: What needs to be done
  
  // AGC: Standard intervals activation
  activeMaintenanceIntervals?: string[]  // AGC: IDs of active standard intervals from maintenanceStandards.ts
  
  // AGC: Metadata
  updatedAt?: string
}

/**
 * AGC: Create maintenance record request
 */
export interface CreateMaintenanceRecordRequest {
  machineId: string
  type: MachineMaintenanceType         // AGC: Primary type (first selected)
  types?: MachineMaintenanceType[]     // AGC: All selected types (multi-select)
  description: string
  date: string
  hoursAtMaintenance: number
  cost?: number
  currency?: string
  provider?: string
  invoiceNumber?: string
  partsReplaced?: MaintenancePart[]
  nextMaintenanceHours?: number
  nextMaintenanceDate?: string
  notes?: string
  attachmentUrls?: string[]
  updatePreventiveSchedule?: boolean   // AGC: If true, update machine's lastMaintenanceHours and reset alarm counters
}

/**
 * AGC: Update maintenance record request
 */
export interface UpdateMaintenanceRecordRequest {
  type?: MachineMaintenanceType
  description?: string
  date?: string
  hoursAtMaintenance?: number
  cost?: number
  currency?: string
  provider?: string
  invoiceNumber?: string
  partsReplaced?: MaintenancePart[]
  nextMaintenanceHours?: number
  nextMaintenanceDate?: string
  notes?: string
  attachmentUrls?: string[]
}

/**
 * AGC: Get maintenance type label
 * AGC: Returns localized label for maintenance type
 */
export const getMaintenanceTypeLabel = (type: MachineMaintenanceType): string => {
  const labels: Record<MachineMaintenanceType, string> = {
    oil_change: 'Cambio de Aceite',
    preventive: 'Mantenimiento Preventivo',
    corrective: 'Mantenimiento Correctivo',
    tire: 'Neumáticos',
    filter: 'Filtros',
    belt: 'Correas',
    hydraulic: 'Sistema Hidráulico',
    electrical: 'Sistema Eléctrico',
    other: 'Otro',
  }
  return labels[type] || type
}

/**
 * AGC: Get maintenance type color for badges
 */
export const getMaintenanceTypeColor = (type: MachineMaintenanceType): string => {
  const colors: Record<MachineMaintenanceType, string> = {
    oil_change: 'bg-amber-500',
    preventive: 'bg-green-500',
    corrective: 'bg-red-500',
    tire: 'bg-gray-500',
    filter: 'bg-blue-500',
    belt: 'bg-purple-500',
    hydraulic: 'bg-cyan-500',
    electrical: 'bg-yellow-500',
    other: 'bg-gray-400',
  }
  return colors[type] || 'bg-gray-500'
}

/**
 * AGC: All maintenance types for dropdown selection
 */
export const MAINTENANCE_TYPES: { id: MachineMaintenanceType; label: string; icon: string }[] = [
  { id: 'oil_change', label: 'Cambio de Aceite', icon: '🛢️' },
  { id: 'preventive', label: 'Mantenimiento Preventivo', icon: '🔧' },
  { id: 'corrective', label: 'Mantenimiento Correctivo', icon: '⚠️' },
  { id: 'tire', label: 'Neumáticos', icon: '🛞' },
  { id: 'filter', label: 'Filtros', icon: '🔽' },
  { id: 'belt', label: 'Correas', icon: '➰' },
  { id: 'hydraulic', label: 'Sistema Hidráulico', icon: '💧' },
  { id: 'electrical', label: 'Sistema Eléctrico', icon: '⚡' },
  { id: 'other', label: 'Otro', icon: '📝' },
]
